import { useMsal } from '@azure/msal-react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, makeStyles, mergeClasses, Spinner, Text } from '@fluentui/react-components';
import { CheckmarkCircleFilled } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { JSX, useState } from 'react';
import { useNavigate } from 'react-router';
import { useStores } from '../../common/hooks';
import { RoleName, UserRole } from '../../models';
import { RoutePaths } from '../../route-paths';
import './SwitchProfilesModal.scss';

interface SwitchProfilesModalProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    maxWidth: '480px',
    borderRadius: '2px',
  },
  actions: {
    justifyContent: 'end',
  }
});

const SwitchProfilesModalImpl: React.FC<SwitchProfilesModalProps> = (props) => {
  const {
    isOpen,
    onDismiss,
  } = props;

  const navigate = useNavigate();

  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    apiEndpointCallStatesStore,
    userStore,
  } = useStores();

  const classes = useStyles();

  const [selectedRole, setSelectedRole] = useState<RoleName>();

  const saveCurrentProfile = async (): Promise<void> => {
    if (!selectedRole) return;

    try {
      await apiStore.startUserImpersonation(instance, selectedRole);
      const role = await apiStore.getUserImpersonation(instance);
      if (role) {
        userStore.setSelectedRole(role);
        if (role === RoleName.Judge || role === RoleName.NominationJudge) {
          navigate(RoutePaths.JudgingDashboard, { replace: true });
        } else if (role === RoleName.Partner) {
          navigate(RoutePaths.Home, { replace: true });
        } else {
          navigate(RoutePaths.ProgramDashboard, { replace: true });
        }
      }
      onDismiss();
    } catch (error) {
      // TODO: handle error
    }
  };

  const isLoading = apiEndpointCallStatesStore.inFlightStatusMap.get('startUserImpersonation') ||
    apiEndpointCallStatesStore.inFlightStatusMap.get('getUserImpersonation');

  const handleSwitch = (): void => {
    saveCurrentProfile();
  };

  const renderUserRole = (userRole: UserRole): JSX.Element => {
    const isCurrentRole = userStore.selectedRole === userRole.typeCode;
    return (
      <button
        key={`${userRole.typeCode}${userRole.displayName}`}
        onClick={() => setSelectedRole(userRole.typeCode)}
        disabled={isCurrentRole}
      >
        <Text
          size={200}
          weight={userRole.typeCode === selectedRole ? 'semibold' : 'regular'}
        >
          {userRole.displayName}
        </Text>
        {
          isCurrentRole && (
            <>
              <Text
                size={200}
                as='i'
              >
                You already have this profile
                <CheckmarkCircleFilled color='#0078D4' />
              </Text>
            </>
          )
        }
      </button >
    )
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) onDismiss();
      }}
    >
      <DialogSurface
        className={mergeClasses(classes.root, 'switch-profiles-modal')}
      >
        {
          isLoading && (
            <Spinner
              label='Loading...'
              labelPosition='below'
            />
          )
        }
        <DialogTitle>
          Switch profile
        </DialogTitle>
        <DialogBody>
          <DialogContent>
            <Text
              as='p'
              weight='semibold'
            >
              Select the profile that you would like to switch to
            </Text>
            <div>
              {userStore.roles.map((userRole) => renderUserRole(userRole))}
            </div>
          </DialogContent>
        </DialogBody>
        <DialogActions
          className={classes.actions}
        >
          <DialogTrigger disableButtonEnhancement>
            <Button appearance='secondary'>Back</Button>
          </DialogTrigger>
          <Button
            appearance='primary'
            onClick={() => handleSwitch()}
            disabled={!selectedRole || userStore.selectedRole === selectedRole}
          >
            Switch profile
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog >
  );
};

export const SwitchProfilesModal = observer(SwitchProfilesModalImpl);
